import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CustomersService } from '../../../../services/customers.service';
import { StatusService } from '../../../../services/status.service';
import { ViaCepService, ViaCepResponse } from '../../../../services/viacep.service';
import { Customer } from '../../../../models/Customer';
import { CustomerAttachment } from '../../../../models/CustomerAttachment';
import { Status } from '../../../../models/Status';
import { CUSTOMER_CATEGORY_OPTIONS } from '../../../../models/CustomerEnums';
import { FormShellTab } from '../../../../shared/components/form-shell/form-shell.component';

@Component({
  selector: 'app-customer-form',
  templateUrl: './customer-form.component.html',
  styleUrls: ['./customer-form.component.css']
})
export class CustomerFormComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  formStep1: FormGroup;
  formStep2: FormGroup;
  formStep3: FormGroup;
  formStep4: FormGroup;
  currentStep = 1;
  activeTab: string = 'identification';
  readonly availableTabs: FormShellTab[] = [
    { id: 'identification', label: 'Identificação', icon: 'fas fa-building', required: false },
    { id: 'contact', label: 'Contato', icon: 'fas fa-address-book', required: false },
    { id: 'status', label: 'Status & Observações', icon: 'fas fa-clipboard-list', required: false },
    { id: 'payments', label: 'Dados Bancários', icon: 'fas fa-credit-card', required: false },
    { id: 'documents', label: 'Documentos', icon: 'fas fa-paperclip', required: false }
  ];
  customerId?: number;
  isSubmitting = false;
  isPF = false; // Propriedade reativa para controlar a interface
  // CEP
  isLoadingCep = false;
  cepError: string | null = null;
  // Anexos
  attachments: CustomerAttachment[] = [];
  isLoadingAttachments = false;
  uploading = false;
  uploadProgress = 0;
  isDragOver = false;
  pendingAttachments: { file: File; documentType?: string }[] = [];
  
  // Tipos de documentos obrigatórios
  documentTypes = [
    { code: 'CNH', label: 'CNH (Carteira Nacional de Habilitação)', icon: 'fa-id-card', required: false },
    { code: 'RG', label: 'RG (Registro Geral)', icon: 'fa-id-card', required: false },
    { code: 'COMPROVANTE_RESIDENCIA', label: 'Comprovante de Residência', icon: 'fa-home', required: false },
    { code: 'OUTROS', label: 'Outros Documentos', icon: 'fa-file', required: false }
  ];
  categoryOptions = CUSTOMER_CATEGORY_OPTIONS;
  readonly contactTypeOptions = ['Principal', 'Comercial', 'Financeiro', 'Suporte', 'Cobrança', 'Outro'];
  // Validação de documento duplicado
  isCheckingDocument = false;
  documentAlreadyExists = false;
  existingCustomerByDocument: Customer | null = null;
  // Validação de RG duplicado
  isCheckingRG = false;
  rgAlreadyExists = false;
  existingCustomerByRG: Customer | null = null;
  // Status management
  statuses: Status[] = [];
  loadingStatuses = false;
  // Timers para debounce
  private documentCheckTimeout: any;
  private rgCheckTimeout: any;

  readonly BANKS_OPTIONS = [
    { value: '001', label: '001 - Banco do Brasil S.A.' },
    { value: '104', label: '104 - Caixa Econômica Federal' },
    { value: '341', label: '341 - Itaú Unibanco S.A.' },
    { value: '237', label: '237 - Banco Bradesco S.A.' },
    { value: '033', label: '033 - Banco Santander (Brasil) S.A.' },
    { value: '260', label: '260 - Nubank (Nu Pagamentos)' },
    { value: '077', label: '077 - Banco Inter' },
    { value: '336', label: '336 - C6 Bank' },
    { value: '290', label: '290 - PagBank (PagSeguro)' },
    { value: '323', label: '323 - Mercado Pago' },
    { value: '380', label: '380 - PicPay' },
    { value: '422', label: '422 - Banco Safra S.A.' },
    { value: '756', label: '756 - Banco Cooperativo do Brasil (Bancoob)' },
    { value: '748', label: '748 - Sicredi' },
    { value: '041', label: '041 - Banrisul' },
    { value: '004', label: '004 - Banco do Nordeste do Brasil S.A.' },
    { value: '021', label: '021 - Banco do Estado do Espírito Santo S.A.' },
    { value: '047', label: '047 - Banco do Estado de Sergipe S.A.' },
    { value: '085', label: '085 - Ailos - Sistema de Cooperativas de Crédito' },
    { value: '097', label: '097 - Credisis - Central de Cooperativas de Crédito' },
    { value: '136', label: '136 - Unicred Central' },
    { value: '212', label: '212 - Banco Original' },
    { value: '218', label: '218 - Banco BS2' },
    { value: '222', label: '222 - Banco Credit Agricole Brasil S.A.' },
    { value: '224', label: '224 - Banco Fibra S.A.' },
    { value: '233', label: '233 - Banco Cifra' },
    { value: '318', label: '318 - Banco BMG S.A.' },
    { value: '356', label: '356 - Banco Real S.A.' },
    { value: '389', label: '389 - Banco Mercantil do Brasil' },
    { value: '399', label: '399 - HSBC Bank Brasil S.A.' },
    { value: '637', label: '637 - Banco Sofisa S.A.' },
    { value: '652', label: '652 - Itaú Unibanco Holding S.A.' },
    { value: '745', label: '745 - Banco Citibank S.A.' },
    { value: '000', label: '000 - Outro (Especificar)' }
  ];

  readonly ACCOUNT_TYPE_OPTIONS = [
    { value: 'Corrente', label: 'Conta Corrente' },
    { value: 'Poupanca', label: 'Conta Poupança' },
    { value: 'Salario', label: 'Conta Salário' },
    { value: 'Pagamento', label: 'Conta de Pagamento' },
    { value: 'Outro', label: 'Outro' }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private customersService: CustomersService,
    private statusService: StatusService,
    private viaCep: ViaCepService,
    private cdr: ChangeDetectorRef
  ) {
    this.formStep1 = this.fb.group({
      personType: ['Juridica'],
      name: [''],
      tradeName: [''],
      documentNumber: [''],
      stateRegistration: [''],
      driverLicenseNumber: [''],
      driverLicenseExpiryDate: ['']
    });
    this.formStep2 = this.fb.group({
      email: [''],
      primaryPhone: [''],
      customerCategory: [''],
      street: [''],
      number: [''],
      complement: [''],
      neighborhood: [''],
      city: [''],
      state: [''],
      zipCode: [''],
      contacts: this.fb.array([this.createContactFormGroup(true)])
    });
    this.formStep3 = this.fb.group({
      notes: [''],
      status: ['']
    });
    this.formStep4 = this.fb.group({
      bankName: [''],
      agency: [''],
      accountType: [''],
      accountNumber: [''],
      accountHolderName: [''],
      pixKey: ['']
    });
  }

  ngOnDestroy(): void {
    // Limpa timeouts quando o componente for destruído
    if (this.documentCheckTimeout) {
      clearTimeout(this.documentCheckTimeout);
    }
    if (this.rgCheckTimeout) {
      clearTimeout(this.rgCheckTimeout);
    }
    // Cancela todas as subscriptions ativas
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    
    // Inicializa o estado
    this.updatePersonTypeState();
    
    // Carrega os status da entidade Customer
    this.loadCustomerStatuses();
    
    // Ajuste dinâmico de validações conforme o tipo de pessoa
    this.formStep1.get('personType')?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe((value) => {
      this.updatePersonTypeState();
      this.updateDocumentValidators();
      // Limpa documento e RG/IE quando muda o tipo
      const docCtrl = this.formStep1.get('documentNumber');
      const stateRegCtrl = this.formStep1.get('stateRegistration');
      if (docCtrl) {
        docCtrl.setValue('', { emitEvent: false });
      }
      if (stateRegCtrl) {
        stateRegCtrl.setValue('', { emitEvent: false });
      }
      // Limpa validações de duplicação
      this.documentAlreadyExists = false;
      this.existingCustomerByDocument = null;
      this.rgAlreadyExists = false;
      this.existingCustomerByRG = null;
    });
    this.updateDocumentValidators();

    if (idParam) {
      this.customerId = Number(idParam);
      this.customersService.getById(this.customerId).pipe(
        takeUntil(this.destroy$)
      ).subscribe(c => {
        this.formStep1.patchValue({ 
          personType: c.personType || 'Juridica',
          name: c.name, 
          tradeName: c.tradeName, 
          documentNumber: this.maskDocument(c.documentNumber || ''),
          stateRegistration: this.isPF && c.stateRegistration 
            ? this.maskRG(c.stateRegistration) 
            : c.stateRegistration,
          driverLicenseNumber: c.driverLicenseNumber || '',
          driverLicenseExpiryDate: c.driverLicenseExpiryDate ? c.driverLicenseExpiryDate.split('T')[0] : ''
        });
        
        // Atualiza o estado APÓS o patchValue
        this.updatePersonTypeState();
        this.updateDocumentValidators();

        this.formStep2.patchValue({ 
          email: (c.email || '').trim().toLowerCase(), 
          primaryPhone: this.maskPhone(c.primaryPhone || ''), 
          customerCategory: c.customerCategory,
          street: c.street,
          number: c.number,
          complement: c.complement,
          neighborhood: c.neighborhood,
          city: c.city,
          state: c.state,
          zipCode: c.zipCode
        });
        if (Array.isArray(c.contacts) && c.contacts.length > 0) {
          this.resetContacts(c.contacts.map(contact => ({
            contactType: contact.contactType || 'Principal',
            contactName: contact.contactName || '',
            phoneNumber: this.maskPhone(contact.phoneNumber || ''),
            secondaryPhoneNumber: this.maskPhone(contact.secondaryPhoneNumber || ''),
            isDefault: !!contact.isDefault
          })));
        }
        this.formStep3.patchValue({ 
          notes: c.notes, 
          status: c.status || ''
        });
        this.formStep4.patchValue({
          bankName: c.bankName || '',
          agency: c.agency || '',
          accountType: c.accountType || '',
          accountNumber: c.accountNumber || '',
          accountHolderName: c.accountHolderName || '',
          pixKey: c.pixKey || ''
        });
        // Carrega anexos quando em edição
        this.loadAttachments();
      });
    }
  }

  nextStep(): void {
    if (this.currentStep < 4) {
      this.currentStep++;
      this.updateTabFromStep();
    }
  }

  private loadCustomerStatuses(): void {
    this.loadingStatuses = true;
    this.statusService.getByEntity('Customer', true).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (statuses: Status[]) => {
        this.statuses = statuses;
        this.loadingStatuses = false;
      },
      error: (err) => {
        console.error('Erro ao carregar status:', err);
        this.loadingStatuses = false;
      }
    });
  }
  
  prevStep(): void { 
    if (this.currentStep > 1) { 
      this.currentStep--; 
      this.updateTabFromStep();
    } 
  }

  goToStep(step: number): void {
    if (step < 1 || step > 4) return;
    this.currentStep = step;
    this.updateTabFromStep();
  }

  selectTab(tabId: string): void {
    this.activeTab = tabId;
    if (tabId === 'identification') {
      this.currentStep = 1;
    } else if (tabId === 'contact') {
      this.currentStep = 2;
    } else if (tabId === 'status') {
      this.currentStep = 3;
    } else if (tabId === 'payments') {
      this.currentStep = 4;
    }
    // Quando tabId for 'documents', não alteramos currentStep:
    // os botões Avançar/Voltar continuam seguindo o fluxo 1-4.
  }

  save(): void {
    this.isSubmitting = true;
    const step3 = this.formStep3.value;
    const step4 = this.formStep4.value;
    const payload = { 
      ...this.formStep1.value, 
      ...this.formStep2.value, 
      ...step3,
      ...step4,
      documentNumber: this.onlyDigits(this.formStep1.get('documentNumber')?.value),
      stateRegistration: this.isPF 
        ? this.onlyDigits(this.formStep1.get('stateRegistration')?.value) 
        : this.formStep1.get('stateRegistration')?.value,
      primaryPhone: this.normalizePhone(this.formStep2.get('primaryPhone')?.value),
      email: this.normalizeEmail(this.formStep2.get('email')?.value),
      status: step3.status,
      contacts: this.mapContactsPayload()
    };
    
    // Remove campos antigos que não são mais usados
    delete payload.active;
    delete payload.blocked;
    if (this.customerId) {
      this.customersService.update(this.customerId, payload).pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: () => { this.isSubmitting = false; this.router.navigate(['/commercial/customers']); },
        error: () => { this.isSubmitting = false; }
      });
    } else {
      this.customersService.create(payload).pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (created) => {
          this.isSubmitting = false;
          const newId = this.extractCustomerId(created);
          if (newId && this.pendingAttachments.length > 0) {
            this.customerId = newId;
            this.uploadPendingAttachmentsAndNavigate();
          } else {
            this.router.navigate(['/commercial/customers']);
          }
        },
        error: () => { this.isSubmitting = false; }
      });
    }
  }

  cancel(): void { this.router.navigate(['/commercial/customers']); }

  // ===== Controle de estado =====
  private updatePersonTypeState(): void {
    const currentType = this.formStep1.get('personType')?.value;
    this.isPF = currentType === 'Fisica';
    this.cdr.detectChanges();
  }

  private updateTabFromStep(): void {
    if (this.currentStep === 1) {
      this.activeTab = 'identification';
    } else if (this.currentStep === 2) {
      this.activeTab = 'contact';
    } else if (this.currentStep === 3) {
      this.activeTab = 'status';
    } else {
      this.activeTab = 'payments';
    }
  }

  private extractCustomerId(created: any): number | null {
    if (!created) return null;
    if (typeof created.id === 'number') return created.id;
    if (typeof created.Id === 'number') return created.Id;
    if (created.data) {
      if (typeof created.data.id === 'number') return created.data.id;
      if (typeof created.data.Id === 'number') return created.data.Id;
    }
    return null;
  }

  private uploadPendingAttachmentsAndNavigate(): void {
    if (!this.customerId || this.pendingAttachments.length === 0) {
      this.router.navigate(['/commercial/customers']);
      return;
    }

    this.uploading = true;
    this.uploadProgress = 0;

    const total = this.pendingAttachments.length;
    let index = 0;

    const uploadNext = () => {
      if (index >= total) {
        this.uploading = false;
        this.uploadProgress = 100;
        this.pendingAttachments = [];
        this.router.navigate(['/commercial/customers']);
        return;
      }

      const { file, documentType } = this.pendingAttachments[index];
      let progressInterval: any = null;
      this.uploadProgress = Math.round((index / total) * 100);
      progressInterval = setInterval(() => {
        if (this.uploadProgress < Math.min(90, Math.round(((index + 0.9) / total) * 100))) {
          this.uploadProgress += 2;
        }
      }, 150);

      this.customersService.uploadAttachment(this.customerId!, file, { category: documentType }).pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: () => {
          clearInterval(progressInterval);
          index++;
          this.uploadProgress = Math.round((index / total) * 100);
          uploadNext();
        },
        error: () => {
          clearInterval(progressInterval);
          index++;
          uploadNext();
        }
      });
    };

    uploadNext();
  }

  // ===== Handlers de máscara =====
  onDriverLicenseInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.maskDriverLicense(input.value || '');
    this.formStep1.get('driverLicenseNumber')?.setValue(masked, { emitEvent: false });
  }

  onDocumentInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.maskDocument(input.value || '');
    this.formStep1.get('documentNumber')?.setValue(masked, { emitEvent: false });
    
    // Limpa timeout anterior
    if (this.documentCheckTimeout) {
      clearTimeout(this.documentCheckTimeout);
    }
    
    // Reseta validação enquanto digita
    this.documentAlreadyExists = false;
    this.existingCustomerByDocument = null;
    
    // Verifica se o documento está completo
    const digits = this.onlyDigits(masked);
    const expectedLength = this.isPF ? 11 : 14;
    
    if (digits.length === expectedLength) {
      // Aguarda 800ms após parar de digitar para validar
      this.documentCheckTimeout = setTimeout(() => {
        this.checkDocumentExists(digits);
      }, 800);
    }
  }

  onPhoneInput(controlName: 'primaryPhone', event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.maskPhone(input.value || '');
    this.formStep2.get(controlName)?.setValue(masked, { emitEvent: false });
  }

  onContactPhoneInput(index: number, controlName: 'phoneNumber' | 'secondaryPhoneNumber', event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.maskPhone(input.value || '');
    const contactGroup = this.contactsFormArray.at(index) as FormGroup | undefined;
    contactGroup?.get(controlName)?.setValue(masked, { emitEvent: false });
  }

  onStateRegistrationInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    
    // Limpa timeout anterior
    if (this.rgCheckTimeout) {
      clearTimeout(this.rgCheckTimeout);
    }
    
    // Se for PF (RG), aplica máscara; se PJ (IE), deixa livre
    if (this.isPF) {
      const masked = this.maskRG(input.value || '');
      this.formStep1.get('stateRegistration')?.setValue(masked, { emitEvent: false });
      
      // Reseta validação enquanto digita
      this.rgAlreadyExists = false;
      this.existingCustomerByRG = null;
      
      // Verifica se o RG está completo (9 dígitos)
      const digits = this.onlyDigits(masked);
      if (digits.length === 9) {
        // Aguarda 800ms após parar de digitar para validar
        this.rgCheckTimeout = setTimeout(() => {
          this.checkRGExists(digits);
        }, 800);
      }
    } else {
      const value = (input.value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
      this.formStep1.get('stateRegistration')?.setValue(value, { emitEvent: false });
    }
  }

  onEmailInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = (input.value || '').trim().toLowerCase();
    this.formStep2.get('email')?.setValue(value, { emitEvent: false });
  }

  get contactsFormArray(): FormArray {
    return this.formStep2.get('contacts') as FormArray;
  }

  addContact(): void {
    this.contactsFormArray.push(this.createContactFormGroup(false));
  }

  removeContact(index: number): void {
    if (this.contactsFormArray.length <= 1) {
      return;
    }
    this.contactsFormArray.removeAt(index);
    if (!this.contactsFormArray.controls.some(control => control.get('isDefault')?.value)) {
      this.setDefaultContact(0);
    }
  }

  setDefaultContact(index: number): void {
    this.contactsFormArray.controls.forEach((control, currentIndex) => {
      control.get('isDefault')?.setValue(currentIndex === index, { emitEvent: false });
    });
  }

  onZipCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.maskCEP(input.value || '');
    this.formStep2.get('zipCode')?.setValue(masked, { emitEvent: false });

    const digits = this.onlyDigits(masked);
    if (digits.length === 8) {
      this.fetchAddressByCEP(digits);
    } else {
      this.cepError = null;
    }
  }

  applyAgencyMask(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');
    if (value.length <= 4) {
      // Sem dígito verificador ainda
      input.value = value;
    } else {
      // Tudo menos o último dígito é a agência, último dígito é o DV
      input.value = value.substring(0, value.length - 1) + '-' + value.substring(value.length - 1);
    }
    this.formStep4.get('agency')?.setValue(input.value, { emitEvent: false });
  }

  applyAccountNumberMask(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');
    if (value.length <= 1) {
      // Sem dígito verificador ainda
      input.value = value;
    } else {
      // Tudo menos o último dígito é o número da conta, último dígito é o DV
      input.value = value.substring(0, value.length - 1) + '-' + value.substring(value.length - 1);
    }
    this.formStep4.get('accountNumber')?.setValue(input.value, { emitEvent: false });
  }

  // ===== Validações e máscaras =====
  private updateDocumentValidators(): void {
    const ctrl = this.formStep1.get('documentNumber');
    ctrl?.clearValidators();
    ctrl?.updateValueAndValidity({ emitEvent: false });
  }

  private maskDriverLicense(value: string): string {
    // Remove tudo que não for dígito e limita a 11
    const digits = value.replace(/\D/g, '');
    return digits.substring(0, 11);
  }

  private maskDocument(value: string): string {
    const digits = this.onlyDigits(value).slice(0, this.isPF ? 11 : 14);
    if (this.isPF) {
      // CPF: 000.000.000-00
      return digits
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    // CNPJ: 00.000.000/0000-00
    return digits
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  }

  private maskPhone(value: string): string {
    const digits = this.onlyDigits(value).slice(0, 11);
    if (digits.length <= 10) {
      // (00) 0000-0000
      return digits
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    // (00) 00000-0000
    return digits
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  }

  private normalizePhone(value: string | undefined): string | undefined {
    const digits = this.onlyDigits(value || '');
    return digits || undefined;
  }

  private normalizeEmail(value: string | undefined): string | undefined {
    const v = (value || '').trim().toLowerCase();
    return v || undefined;
  }

  private createContactFormGroup(isDefault: boolean): FormGroup {
    return this.fb.group({
      contactType: ['Principal'],
      contactName: [''],
      phoneNumber: [''],
      secondaryPhoneNumber: [''],
      isDefault: [isDefault]
    });
  }

  private resetContacts(
    contacts: Array<{
      contactType: string;
      contactName: string;
      phoneNumber: string;
      secondaryPhoneNumber?: string;
      isDefault: boolean;
    }>
  ): void {
    const controls = contacts.length > 0 ? contacts : [{
      contactType: 'Principal',
      contactName: '',
      phoneNumber: '',
      secondaryPhoneNumber: '',
      isDefault: true
    }];

    this.formStep2.setControl('contacts', this.fb.array(
      controls.map((contact, index) => this.fb.group({
        contactType: [contact.contactType || 'Principal'],
        contactName: [contact.contactName || ''],
        phoneNumber: [contact.phoneNumber || ''],
        secondaryPhoneNumber: [contact.secondaryPhoneNumber || ''],
        isDefault: [contact.isDefault || index === 0]
      }))
    ));
  }

  private mapContactsPayload(): Array<{
    contactType: string;
    contactName: string;
    phoneNumber: string;
    secondaryPhoneNumber?: string;
    isDefault: boolean;
  }> {
    const contacts = this.contactsFormArray.controls
      .map(control => ({
        contactType: control.get('contactType')?.value || 'Principal',
        contactName: (control.get('contactName')?.value || '').trim(),
        phoneNumber: this.normalizePhone(control.get('phoneNumber')?.value) || '',
        secondaryPhoneNumber: this.normalizePhone(control.get('secondaryPhoneNumber')?.value) || undefined,
        isDefault: !!control.get('isDefault')?.value
      }))
      .filter(contact => !!contact.contactName && !!contact.phoneNumber);

    if (contacts.length === 0) {
      const fallbackPhone = this.normalizePhone(this.formStep2.get('primaryPhone')?.value);
      const fallbackEmail = this.normalizeEmail(this.formStep2.get('email')?.value);
      if (fallbackPhone) {
        return [{
          contactType: 'Principal',
          contactName: (this.formStep1.get('name')?.value || 'Contato Principal').trim(),
          phoneNumber: fallbackPhone,
          isDefault: true
        }];
      }
      return [];
    }

    if (!contacts.some(contact => contact.isDefault)) {
      contacts[0].isDefault = true;
    }

    return contacts;
  }

  private onlyDigits(value: string | undefined): string {
    return (value || '').replace(/\D+/g, '');
  }

  private maskCEP(value: string): string {
    const digits = this.onlyDigits(value).slice(0, 8);
    return digits.replace(/(\d{5})(\d)/, '$1-$2');
  }

  private maskRG(value: string): string {
    const digits = this.onlyDigits(value).slice(0, 9);
    // RG: XX.XXX.XXX-X (9 dígitos)
    return digits
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1})$/, '$1-$2');
  }

  private fetchAddressByCEP(cepDigits: string): void {
    if (!cepDigits || cepDigits.length !== 8) return;
    this.isLoadingCep = true;
    this.cepError = null;
    this.viaCep.lookup(cepDigits).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (res: ViaCepResponse) => {
        this.isLoadingCep = false;
        if ((res as any)?.erro) {
          this.cepError = 'CEP não encontrado.';
          return;
        }
        this.formStep2.patchValue({
          street: res.logradouro || this.formStep2.get('street')?.value,
          neighborhood: res.bairro || this.formStep2.get('neighborhood')?.value,
          city: res.localidade || this.formStep2.get('city')?.value,
          state: res.uf || this.formStep2.get('state')?.value,
          complement: res.complemento || this.formStep2.get('complement')?.value
        }, { emitEvent: false });
      },
      error: _ => {
        this.isLoadingCep = false;
        this.cepError = 'Erro ao consultar o CEP.';
      }
    });
  }

  private checkDocumentExists(documentDigits: string): void {
    if (!documentDigits) return;
    
    this.isCheckingDocument = true;
    this.documentAlreadyExists = false;
    this.existingCustomerByDocument = null;

    this.customersService.getByDocument(documentDigits).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (customer: Customer | null) => {
        this.isCheckingDocument = false;
        
        if (customer) {
          // Se estiver editando o próprio cliente, ignora
          if (this.customerId && customer.id === this.customerId) {
            this.documentAlreadyExists = false;
            this.existingCustomerByDocument = null;
          } else {
            // Documento já existe em outro cliente
            this.documentAlreadyExists = true;
            this.existingCustomerByDocument = customer;
          }
        } else {
          // Documento não existe, tudo ok
          this.documentAlreadyExists = false;
          this.existingCustomerByDocument = null;
        }
      },
      error: (err) => {
        console.error('Erro ao verificar documento:', err);
        this.isCheckingDocument = false;
        this.documentAlreadyExists = false;
        this.existingCustomerByDocument = null;
      }
    });
  }

  private checkRGExists(rgDigits: string): void {
    if (!rgDigits) return;
    
    this.isCheckingRG = true;
    this.rgAlreadyExists = false;
    this.existingCustomerByRG = null;

    this.customersService.getByStateRegistration(rgDigits).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (customer: Customer | null) => {
        this.isCheckingRG = false;
        
        if (customer) {
          // Se estiver editando o próprio cliente, ignora
          if (this.customerId && customer.id === this.customerId) {
            this.rgAlreadyExists = false;
            this.existingCustomerByRG = null;
          } else {
            // RG já existe em outro cliente
            this.rgAlreadyExists = true;
            this.existingCustomerByRG = customer;
          }
        } else {
          // RG não existe, tudo ok
          this.rgAlreadyExists = false;
          this.existingCustomerByRG = null;
        }
      },
      error: (err) => {
        console.error('Erro ao verificar RG:', err);
        this.isCheckingRG = false;
        this.rgAlreadyExists = false;
        this.existingCustomerByRG = null;
      }
    });
  }

  // ===== Anexos =====
  loadAttachments(): void {
    if (!this.customerId) return;
    this.isLoadingAttachments = true;
    this.customersService.listAttachments(this.customerId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: list => { this.attachments = list || []; this.isLoadingAttachments = false; },
      error: _ => { this.isLoadingAttachments = false; }
    });
  }

  onAttachmentFileSelected(event: Event, documentType?: string): void {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    if (!files.length) return;
    if (!this.customerId) {
      files.forEach(file => this.pendingAttachments.push({ file, documentType }));
    } else {
      this.uploadFiles(files, documentType);
    }
    if (input) input.value = '';
  }

  onAttachmentDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onAttachmentDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  onAttachmentDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    const files = event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : [];
    if (!files.length) return;
    if (!this.customerId) {
      files.forEach(file => this.pendingAttachments.push({ file }));
    } else {
      this.uploadFiles(files);
    }
  }
                                              
  private uploadFiles(files: File[], category?: string): void {
    if (!this.customerId || files.length === 0) return;
    this.uploading = true;
    this.uploadProgress = 0;

    const total = files.length;
    let index = 0;

    const uploadNext = () => {
      if (index >= total) {
        this.uploading = false;
        this.uploadProgress = 100;
        this.loadAttachments();
        return;
      }

      const file = files[index];
      let progressInterval: any = null;
      this.uploadProgress = Math.round((index / total) * 100);
      progressInterval = setInterval(() => {
        if (this.uploadProgress < Math.min(90, Math.round(((index + 0.9) / total) * 100))) {
          this.uploadProgress += 2;
        }
      }, 150);

      this.customersService.uploadAttachment(this.customerId!, file, { category }).pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: _ => {
          clearInterval(progressInterval);
          index++;
          this.uploadProgress = Math.round((index / total) * 100);
          uploadNext();
        },
        error: _ => {
          clearInterval(progressInterval);
          index++;
          uploadNext();
        }
      });
    };

    uploadNext();
  }

  getAttachmentsByType(type: string): CustomerAttachment[] {
    const existing = this.attachments.filter(a => a.category === type);
    const pending = this.pendingAttachments
      .filter(p => p.documentType === type)
      .map(p => ({
        id: 0,
        customerId: this.customerId ?? 0,
        blobUrl: '',
        originalFileName: p.file.name,
        contentType: p.file.type || 'application/octet-stream',
        fileSize: p.file.size,
        category: type,
        active: true,
        isPending: true
      } as CustomerAttachment));
    return [...existing, ...pending];
  }

  removeAttachment(att: CustomerAttachment): void {
    if (!att) return;

    if (att.isPending) {
      this.pendingAttachments = this.pendingAttachments.filter(
        p => !(p.file.name === att.originalFileName && p.documentType === att.category)
      );
      return;
    }

    if (!att.id) return;
    this.customersService.removeAttachment(att.id).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: _ => this.loadAttachments(),
      error: _ => {}
    });
  }
}


